import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaArrowRight,
  FaQuoteLeft,
  FaQrcode
} from 'react-icons/fa';
import heroBanner from '../assets/hero banner.jpeg';
import { useLanguage } from '../context/LanguageContext';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showQRModal, setShowQRModal] = useState(false);
  
  const impactStats = [
    { image: '/envconservation.jpeg', value: '50,000+', labelKey: 'treesPlanted', color: 'text-primary-green' },
    { image: '/orphangehosuse.jpg', value: '2,500+', labelKey: 'girlsSupported', color: 'text-pink-500' },
    { image: '/cowwelfare.png', value: '1,200+', labelKey: 'cowsRescued', color: 'text-amber-600' },
    { image: '/naturaldisater.jpg', value: '150+', labelKey: 'disasterReliefCampaigns', color: 'text-blue-500' },
    { image: '/oldage.png', value: '800+', labelKey: 'oldAgeResidents', color: 'text-purple-500' },
  ];

  const services = [
    { id: 'tree', image: '/envconservation.jpeg', titleKey: 'serviceTreeTitle', descKey: 'serviceTreeDesc', link: '/services#tree' },
    { id: 'cow', image: '/cowwelfare.png', titleKey: 'serviceCowTitle', descKey: 'serviceCowDesc', link: '/services#cow' },
    { id: 'girls', image: '/orphangehosuse.jpg', titleKey: 'serviceGirlsTitle', descKey: 'serviceGirlsDesc', link: '/services#girls' },
    { id: 'disaster', image: '/naturaldisater.jpg', titleKey: 'serviceDisasterTitle', descKey: 'serviceDisasterDesc', link: '/services#disaster' },
    { id: 'oldage', image: '/oldage.png', titleKey: 'serviceOldageTitle', descKey: 'serviceOldageDesc', link: '/services#oldage' },
    { id: 'meditation', image: '/meditation.jpg', titleKey: 'serviceMeditationTitle', descKey: 'serviceMeditationDesc', link: '/services#meditation' },
    { id: 'heritage', image: '/historical.png', titleKey: 'serviceHeritageTitle', descKey: 'serviceHeritageDesc', link: '/services#heritage' },
    { id: 'legal', image: '/leagalaid.webp', titleKey: 'serviceLegalTitle', descKey: 'serviceLegalDesc', link: '/services#legal' },
    { id: 'organic', image: '/organic.jpg', titleKey: 'serviceOrganicTitle', descKey: 'serviceOrganicDesc', link: '/services#organic' },
  ];

  const testimonials = [
    { name: 'Rahul Sharma', role: 'Donor', content: 'Transparent donation process and regular updates on how my contributions are being used.' },
    { name: 'Priya Patel', role: 'Volunteer', content: 'Amazing organization to volunteer with. The impact we create together is truly inspiring.' },
    { name: 'Dr. Amit Kumar', role: 'Beneficiary', content: 'The scholarship program helped my daughter complete her education. Thank you!' },
  ];

  return (
    <div className="home-page">
      {/* Hero Banner */}
      <div className="w-full">
        <img 
          src={heroBanner} 
          alt="Together We Can Build a Better Future" 
          className="w-full h-auto object-contain"
        />
        <div className="bg-gradient-to-r from-primary-green/10 to-primary-blue/10 py-8 px-4 md:px-8">
          <div className="container-lg mx-auto">
            <p className="text-gray-800 text-base md:text-lg leading-relaxed text-justify max-w-6xl mx-auto">
              <strong>Founded in 2019 with experience rooted in a legacy since 1972, 
              Vishwa Bandhu Welfare Foundation emerged from a simple yet powerful vision — 
              to create positive social change through compassionate service and sustainable development 
              initiatives as a trusted welfare foundation committed to uplifting communities and fostering lasting impact.
              </strong>
            </p>
          </div>
        </div>
      </div>


      {/* Impact Statistics */}
      <section className="section-padding bg-gray-50">
        <div className="container-lg px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('ourImpactNumbers')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('impactSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {impactStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -8, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`stat-card relative rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden h-56 sm:h-64`}
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img 
                    src={stat.image} 
                    alt={t(stat.labelKey)}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30"></div>
                </div>
                
                {/* Content */}
                <div className="relative z-10 h-full flex flex-col items-center justify-center p-3 sm:p-6 text-center">
                  <div className={`text-3xl sm:text-4xl md:text-5xl font-black mb-2 sm:mb-3 text-white drop-shadow-lg leading-tight`}>{stat.value}</div>
                  <div className="text-white font-bold text-xs sm:text-sm drop-shadow-md px-1">{t(stat.labelKey)}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="section-padding bg-gradient-to-b from-gray-50 to-white">
        <div className="container-lg px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('ourServicesCauses')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('servicesSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group h-full"
              >
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 h-full flex flex-col border border-gray-100 overflow-hidden hover:border-primary-green/30">
                  {/* Image Background Header */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={t(service.titleKey)}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent"></div>
                    <h3 className="absolute bottom-4 left-4 right-4 text-xl font-bold text-white z-10 drop-shadow-lg">{t(service.titleKey)}</h3>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex-1 flex flex-col bg-white">
                    <p className="text-gray-700 mb-6 leading-relaxed">{t(service.descKey)}</p>
                    
                    {/* Actions */}
                    <div className="flex gap-3 mt-auto pt-4 border-t border-gray-100">
                      <Link 
                        to={service.link} 
                        className="flex-1 text-center bg-gradient-to-r from-primary-green/10 to-primary-blue/10 text-primary-green font-bold py-3 rounded-lg hover:from-primary-green/20 hover:to-primary-blue/20 transition-all duration-300 flex items-center justify-center gap-2 group/link"
                      >
                        {t('learnMore')} <span className="group-hover/link:translate-x-1 transition-transform"><FaArrowRight /></span>
                      </Link>
                      <button
                        onClick={() => navigate('/donation', { state: { selectedService: service.id } })}
                        className="flex-1 bg-gradient-to-r from-primary-green to-primary-blue text-white font-bold py-3 rounded-lg hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-300 flex items-center justify-center gap-2"
                      >
                        Donate Us
                      </button>
                    </div>
                  </div>

                  {/* Bottom accent */}
                  <div className="h-1 bg-gradient-to-r from-primary-green to-primary-blue opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services" className="btn-accent text-lg py-3 px-8 hover:scale-105 transition-transform duration-300">
              {t('viewAllServices')}
            </Link>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="section-padding bg-gradient-to-r from-primary-green/5 to-primary-blue/5">
        <div className="container-lg px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Success Stories</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Real stories of transformation and impact from our beneficiaries
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg p-6"
              >
                <div className="text-primary-orange text-3xl mb-4">
                  <FaQuoteLeft />
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center">
                  <div className="bg-gray-200 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                    <span className="font-bold text-gray-700">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="font-bold text-gray-800">{testimonial.name}</div>
                    <div className="text-gray-600 text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding bg-gradient-to-r from-primary-green to-primary-blue">
        <div className="container-lg px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Make a Difference?
            </h2>
            <p className="text-white/90 text-xl mb-8 max-w-2xl mx-auto">
              Your support can transform lives and create lasting impact. Join us in building a better future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setShowQRModal(true)}
                className="bg-white text-primary-green hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105"
              >
                Donate Us
              </button>
              <Link to="/volunteer" className="bg-primary-orange hover:bg-orange-600 text-white font-semibold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105">
                Become a Volunteer
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="section-padding bg-gray-50">
        <div className="container-lg px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
            <p className="text-gray-600 mb-8">
              Subscribe to our newsletter to receive updates about our activities, success stories, and upcoming events.
            </p>
            <form className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-grow px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-green"
              />
              <button
                type="submit"
                className="btn-primary py-3 px-8"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* QR Code Modal */}
      {showQRModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
          onClick={() => setShowQRModal(false)}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden relative my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowQRModal(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors text-gray-600 hover:text-gray-800"
            >
              <span className="text-2xl leading-none">×</span>
            </button>

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary-green to-primary-blue p-6 text-white text-center">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-5xl mb-3"
              >
                <FaQrcode className="mx-auto" />
              </motion.div>
              <h3 className="text-2xl font-bold mb-2">Scan & Donate</h3>
              <p className="text-white/90 text-sm">
                Support Vishwa Bandhu Welfare Foundation
              </p>
            </div>

            {/* QR Code Display */}
            <div className="p-6">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-4 shadow-inner">
                <div className="w-full max-w-md mx-auto aspect-square flex items-center justify-center bg-white rounded-xl shadow-lg p-4">
                  <img 
                    src="/qrscanner.png" 
                    alt="QR Code for Donation" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* UPI ID Only */}
              <div className="mt-4 text-center px-2">
                <div className="font-bold text-gray-800 text-lg mb-1">UPI ID</div>
                <div className="text-primary-green font-semibold text-base sm:text-xl break-all">vishwabandhuwelfaref.82170069@hdfcbank</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default HomePage;